/**
 * 🚲 YouBikeCabinet — YouBike 站點柜（與 POI 柜並列）
 * 僅本機靜態站：世代 1.0／2.0 ＋車柱容量（不打 TDX Availability）
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { appendMapClientQuery, isCompactMapClient } from '../../utils/map-client-profile.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';

const YB_MIN_ZOOM_COMPACT = 12;

function esc(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** ISO／+08:00 → 顯示用 HKT 字串 */
function formatHkt(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const parts = new Intl.DateTimeFormat('zh-TW', {
        timeZone: 'Asia/Hong_Kong',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')} HKT`;
}

class YoubikeCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.layer = null;
        this._fc = null;
        this.genFilters = new Set();
        this._viewportTimer = null;
        this.loadAssets('/js/cabinets/youbike');
    }

    onMount() {
        this.ensureGlobalStyles();
        const toggle = this.shadowRoot.getElementById('toggleYoubike');

        toggle?.addEventListener('change', (e) => {
            if (e.target.checked) {
                if (!this.genFilters.size) {
                    this.shadowRoot.querySelectorAll('[data-yb-gen]').forEach((el) => {
                        el.checked = true;
                    });
                    this.syncGenFilters();
                }
                this.loadAndRender();
            } else {
                this.clearLayer();
                this.clearGenChecks();
            }
            this.syncSubToggleState();
        });

        this.shadowRoot.querySelectorAll('[data-yb-gen]').forEach((el) => {
            el.addEventListener('change', () => {
                this.syncGenFilters();
                if (toggle?.checked) this.loadAndRender();
            });
        });
    }

    afterAssetsMounted() {
        this.syncSubToggleState();
    }

    onMapReady(map) {
        this.map = map;
        this.ensureGlobalStyles();
        const onViewport = () => {
            const on = !!this.shadowRoot?.getElementById('toggleYoubike')?.checked;
            if (!on) return;
            clearTimeout(this._viewportTimer);
            this._viewportTimer = setTimeout(() => this.loadAndRender(), 280);
        };
        this.map.on('moveend', onViewport);
        this.map.on('zoomend', onViewport);
    }

    onUnmount() {
        this.clearLayer();
    }

    ensureGlobalStyles() {
        if (document.querySelector('link[data-youbike-map-css]')) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/css/youbike-map-overlays.css?v=17.0.817';
        link.setAttribute('data-youbike-map-css', '17.0.817');
        document.head.appendChild(link);
    }

    syncSubToggleState() {
        const on = !!this.shadowRoot?.getElementById('toggleYoubike')?.checked;
        this.shadowRoot?.querySelectorAll('[data-yb-gen]').forEach((el) => {
            el.disabled = !on;
            if (!on) el.checked = false;
        });
        if (!on) this.genFilters.clear();
    }

    syncGenFilters() {
        this.genFilters = new Set(
            [...(this.shadowRoot?.querySelectorAll('[data-yb-gen]:checked') || [])]
                .map((el) => el.getAttribute('data-yb-gen'))
                .filter(Boolean)
        );
    }

    clearGenChecks() {
        this.shadowRoot?.querySelectorAll('[data-yb-gen]').forEach((el) => {
            el.checked = false;
        });
        this.genFilters.clear();
    }

    setStatus(text, { hint = false } = {}) {
        const el = this.shadowRoot?.getElementById('status-text');
        if (!el) return;
        el.innerText = text;
        el.classList.toggle('is-hint', !!hint);
    }

    bboxQuery() {
        const bounds = this.map.getBounds().pad(0.12);
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const params = new URLSearchParams({
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat),
            zoom: String(this.map.getZoom())
        });
        if (this.genFilters.size) {
            params.set('generation', [...this.genFilters].join(','));
        }
        return appendMapClientQuery(params);
    }

    markerHtml(props) {
        const gen = props.h_youbike_generation || '?';
        const genClass = gen === '1.0' ? 'gen-1' : gen === '2.0' ? 'gen-2' : 'gen-x';
        const cap = props.h_bikes_capacity;
        const countText = cap != null ? String(cap) : '—';
        return `<div class="yb-mark" title="${esc(props.h_name || '')}">
            <span class="yb-badge ${genClass}">${esc(gen)}</span>
            <span class="yb-count is-capacity">${esc(countText)}</span>
        </div>`;
    }

    markerIcon(props) {
        return L.divIcon({
            className: 'custom-youbike-mark',
            html: this.markerHtml(props),
            iconSize: [64, 18],
            iconAnchor: [12, 9]
        });
    }

    buildPopupHtml(props, latlng = null) {
        const name = esc(props.h_name || 'YouBike 站');
        const addr = esc(props.h_address || '');
        const label = esc(props.h_service_label || `YouBike${props.h_youbike_generation || ''}`);
        const cap = props.h_bikes_capacity;
        const staticTs = formatHkt(props.update_time);
        const navBtn = googleMapsNavButtonHtml(latlng || props);

        return `
            <div class="yb-popup-inner">
                <div class="yb-popup-kicker">🚲 YouBike</div>
                <div class="yb-title">${name}</div>
                ${addr ? `<div class="yb-meta">📍 ${addr}</div>` : ''}
                ${navBtn}
                <div class="yb-meta"><b>世代：</b>${label}</div>
                <div class="yb-meta"><b>車柱容量：</b>${cap != null ? esc(cap) : '—'}</div>
                <div class="yb-popup-note">標記數字為車柱容量（靜態），不含即時可租數。</div>
                <div class="yb-popup-source">
                    ${staticTs ? `靜態站資料：${esc(staticTs)}` : '資料來源：本機 YouBike 靜態站'}
                </div>
            </div>
        `;
    }

    clearLayer(updateStatus = true) {
        if (this.map && this.layer) {
            this.map.removeLayer(this.layer);
            this.layer = null;
        }
        this._fc = null;
        if (updateStatus) this.setStatus('已隱藏');
    }

    mountGeoJson(fc) {
        if (!this.map) return;
        this.clearLayer(false);
        const features = Array.isArray(fc?.features) ? fc.features : [];
        const geo = L.geoJSON(
            { type: 'FeatureCollection', features },
            {
                pointToLayer: (feature, latlng) =>
                    L.marker(latlng, { icon: this.markerIcon(feature.properties || {}) }),
                onEachFeature: (feature, layer) => {
                    layer.bindPopup(this.buildPopupHtml(feature.properties || {}, feature), {
                        className: 'yb-popup',
                        maxWidth: 320
                    });
                }
            }
        );
        if (isCompactMapClient() && typeof L.markerClusterGroup === 'function') {
            const cluster = L.markerClusterGroup({
                maxClusterRadius: 48,
                disableClusteringAtZoom: 16,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: false
            });
            cluster.addLayer(geo);
            cluster.addTo(this.map);
            this.layer = cluster;
        } else {
            geo.addTo(this.map);
            this.layer = geo;
        }
    }

    ensureMarkerClusterLib() {
        if (this._clusterLibsLoaded && typeof L?.markerClusterGroup === 'function') {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            if (!document.querySelector('link[data-yb-mc-css="1"]')) {
                for (const href of [
                    '/lib/markercluster/MarkerCluster.css',
                    '/lib/markercluster/MarkerCluster.Default.css'
                ]) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = href;
                    link.setAttribute('data-yb-mc-css', '1');
                    document.head.appendChild(link);
                }
            }
            if (typeof L?.markerClusterGroup === 'function') {
                this._clusterLibsLoaded = true;
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = '/lib/markercluster/leaflet.markercluster.js';
            script.onload = () => {
                this._clusterLibsLoaded = true;
                resolve();
            };
            script.onerror = () => reject(new Error('markercluster load failed'));
            document.head.appendChild(script);
        });
    }

    parseStationsResponse(res, body) {
        if (!res.ok) {
            const code = body?.error || `HTTP ${res.status}`;
            if (res.status === 404 || code === 'YOUBIKE_STATIONS_NOT_FOUND') {
                throw new Error('站點熱檔尚未掛載');
            }
            throw new Error(typeof code === 'string' ? code : `HTTP ${res.status}`);
        }
        if (!body || typeof body !== 'object' || !Array.isArray(body.features)) {
            throw new Error('回應格式無效（非 FeatureCollection）');
        }
        return body;
    }

    async loadAndRender() {
        if (!this.map) return;
        if (isCompactMapClient() && this.map.getZoom() < YB_MIN_ZOOM_COMPACT) {
            this.clearLayer(false);
            this.setStatus(`請放大至 zoom ${YB_MIN_ZOOM_COMPACT}+`, { hint: true });
            return;
        }
        this.syncGenFilters();
        if (!this.genFilters.size) {
            this.clearLayer(false);
            this.setStatus('請至少勾選 1.0 或 2.0', { hint: true });
            return;
        }
        this.setStatus('載入中…');
        try {
            if (isCompactMapClient()) {
                await this.ensureMarkerClusterLib().catch((e) =>
                    console.warn('[YoubikeCabinet] markercluster unavailable', e)
                );
            }
            const qs = this.bboxQuery();
            const res = await fetch(`/api/live/youbike/stations?${qs}`, {
                credentials: 'include',
                cache: 'default'
            });
            let body = null;
            try {
                body = await res.json();
            } catch {
                body = null;
            }
            this._fc = this.parseStationsResponse(res, body);
            this.mountGeoJson(this._fc);
            const n = this._fc?.features?.length || 0;
            const genHint = [...this.genFilters].join('+');
            const trunc = this._fc?._stats?.truncated ? ' · 已截斷' : '';
            this.setStatus(`✅ ${n} 站（${genHint}）· 容量${trunc}`, { hint: !!trunc });
        } catch (err) {
            this.clearLayer(false);
            this.setStatus(`⚠️ 載入失敗：${err.message || err}`, { hint: true });
        }
    }
}

customElements.define('youbike-cabinet', YoubikeCabinet);

function dock() {
    if (!window.appCore) return;
    const container = document.getElementById('socket-youbike');
    if (!container) return;
    const instance = document.createElement('youbike-cabinet');
    container.innerHTML = '';
    container.appendChild(instance);
    window.appCore.registerCabinet('youbike', instance);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
